import { useEffect, useState } from "react";
import { fetchAuthMe, logoutAdmin } from "@/lib/api";
import AdminShell from "@/pages/admin/AdminShell";
import ApplicationsTab from "@/pages/admin/tabs/ApplicationsTab";
import HostEventsTab from "@/pages/admin/tabs/HostEventsTab";
import EmailsTab from "@/pages/admin/tabs/EmailsTab";
import CohortsTab from "@/pages/admin/tabs/CohortsTab";
import TilesTab from "@/pages/admin/tabs/TilesTab";
import CityCardsTab from "@/pages/admin/tabs/CityCardsTab";
import PlaybookTab from "@/pages/admin/tabs/PlaybookTab";
import AssetsTab from "@/pages/admin/tabs/AssetsTab";

const TAB_REGISTRY = {
    applications: ApplicationsTab,
    "host-events": HostEventsTab,
    emails: EmailsTab,
    cohorts: CohortsTab,
    tiles: TilesTab,
    "city-cards": CityCardsTab,
    playbook: PlaybookTab,
    assets: AssetsTab,
};

export default function AdminPage() {
    const [stage, setStage] = useState("checking");
    const [user, setUser] = useState(null);
    const [tab, setTab] = useState(() => {
        const saved = localStorage.getItem("vc_admin_tab");
        return saved && TAB_REGISTRY[saved] ? saved : "applications";
    });

    useEffect(() => {
        document.title = "Admin · Vibecity";
        let alive = true;
        fetchAuthMe()
            .then((u) => alive && (setUser(u), setStage("authed")))
            .catch(() => alive && setStage("login"));
        return () => {
            alive = false;
        };
    }, []);

    useEffect(() => {
        localStorage.setItem("vc_admin_tab", tab);
    }, [tab]);

    const startGoogleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/admin";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
            redirectUrl,
        )}`;
    };

    const onLogout = async () => {
        try {
            await logoutAdmin();
        } catch (e) {}
        setUser(null);
        setStage("login");
    };

    if (stage === "checking") {
        return (
            <main
                data-testid="admin-page"
                className="min-h-screen bg-background text-ink grid place-items-center"
            >
                <div className="text-ink-soft">Checking session…</div>
            </main>
        );
    }

    if (stage === "login") {
        return (
            <main
                data-testid="admin-page"
                className="min-h-screen bg-background text-ink grid place-items-center p-6"
            >
                <LoginPanel onLogin={startGoogleLogin} />
            </main>
        );
    }

    const TabComponent = TAB_REGISTRY[tab] || ApplicationsTab;

    return (
        <main data-testid="admin-page" className="min-h-screen">
            <AdminShell
                user={user}
                currentKey={tab}
                onNavigate={setTab}
                onLogout={onLogout}
            >
                <TabComponent />
            </AdminShell>
        </main>
    );
}

function LoginPanel({ onLogin }) {
    return (
        <div className="max-w-xl w-full">
            <div className="vc-section-label text-[#5fe3c9] mb-3">
                Restricted
            </div>
            <h1
                className="font-display font-black uppercase tracking-tightest leading-[0.95] mb-6"
                style={{ fontSize: "clamp(2rem, 5vw, 4.2rem)" }}
            >
                Admin sign-in.
            </h1>
            <p className="text-ink-soft text-lg leading-relaxed mb-10 max-w-md">
                Only{" "}
                <span className="text-ink font-display">@emergent.sh</span>{" "}
                Google accounts can use this dashboard.
            </p>
            <button
                data-testid="admin-google-login"
                type="button"
                onClick={onLogin}
                className="inline-flex items-center justify-center gap-3 px-6 py-4 bg-[#f4f4f0] hover:bg-gradient-cyan text-[#0a0a0a] font-display font-bold uppercase tracking-[0.18em] text-[0.82rem] transition-colors rounded-full"
            >
                <GoogleG />
                Continue with Google
            </button>
            <p className="text-[0.7rem] tracking-[0.18em] uppercase font-display font-bold text-ink-soft/70 mt-8">
                Non-emergent accounts will be rejected.
            </p>
        </div>
    );
}

function GoogleG() {
    return (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
            <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.56c2.08-1.92 3.28-4.74 3.28-8.1z"
            />
            <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.56-2.77c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
            />
            <path
                fill="#FBBC05"
                d="M5.84 14.11A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.11V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z"
            />
            <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.07.56 4.21 1.65l3.16-3.16C17.45 2.06 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
            />
        </svg>
    );
}
